import { createColumnHelper } from '@tanstack/react-table';
import { Check, ChevronsUpDown } from 'lucide-react';
import { useCallback, useMemo, useRef, useState } from 'react';

import { EnrollioTable } from '~shared/components/enrollio-table';
import { DataTableColumnHeader } from '~shared/components/table/header';
import { cn } from '~shared/shadcn/lib/utils';
import { Button } from '~shared/shadcn/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '~shared/shadcn/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~shared/shadcn/ui/popover';

import {
  type ClassColumnMappingTableData,
  type ClassCSVHeader,
  NOT_PROVIDED,
  REQUIRED_CLASS_HEADERS,
} from './types';
import type { ClassData } from './validation';

const columnHelper = createColumnHelper<ClassColumnMappingTableData>();

export interface ColumnMappingProps {
  setStep: React.Dispatch<
    React.SetStateAction<'select' | 'mapping' | 'validate'>
  >;
  columnHeaders: Array<string>;
  csvData: Array<ClassData>;
  setCSVData: React.Dispatch<React.SetStateAction<Array<ClassData>>>;

  columnMappingTableData: Array<ClassColumnMappingTableData>;
  setColumnMappingTableData: React.Dispatch<
    React.SetStateAction<Array<ClassColumnMappingTableData>>
  >;
}

export const ColumnMapping = ({
  setStep,
  columnHeaders,
  csvData,
  setCSVData,
  columnMappingTableData,
  setColumnMappingTableData,
}: ColumnMappingProps) => {
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const columns = useMemo(() => {
    const getColumnValue = (header?: string) => {
      if (!header || header === NOT_PROVIDED) return '';
      const csvRow = csvData[0];
      if (!csvRow) return '';
      return csvRow[header as keyof ClassData];
    };

    return [
      columnHelper.accessor('header', {
        header: (info) => (
          <DataTableColumnHeader
            title='Column Headers'
            column={info.column}
            className='pl-4 text-black'
          />
        ),
        cell: (info) => <div className='px-4'>{info.getValue()}</div>,
      }),
      columnHelper.accessor('sample', {
        header: (info) => (
          <DataTableColumnHeader
            title='Sample Values'
            column={info.column}
            className='pl-4 text-black'
          />
        ),
        cell: (info) => <div className='px-4'>{info.getValue()}</div>,
      }),
      columnHelper.accessor('selectedColumn', {
        header: (info) => (
          <DataTableColumnHeader
            title='Select Column Mapping'
            column={info.column}
            className='pl-4 text-black'
          />
        ),
        cell: (info) => {
          const row = info.row.original;
          const isRequired = (
            REQUIRED_CLASS_HEADERS as ReadonlyArray<string>
          ).includes(row.header);
          return (
            <div className='px-4'>
              <SelectColumnCell
                value={row.selectedColumn}
                onChange={(value) => {
                  setColumnMappingTableData((prev) =>
                    prev.map((r) => {
                      if (r.header === row.header) {
                        return {
                          ...r,
                          selectedColumn: value,
                          columnValue: getColumnValue(value),
                        } as ClassColumnMappingTableData;
                      }
                      return r;
                    }),
                  );
                }}
                boundaryElement={tableContainerRef.current}
                headers={columnHeaders}
                isRequired={isRequired}
              />
            </div>
          );
        },
      }),
      columnHelper.accessor('columnValue', {
        header: (info) => (
          <DataTableColumnHeader
            title='Column Values'
            column={info.column}
            className='pl-4 text-black'
          />
        ),
        cell: (info) => (
          <div className='px-4'>
            {info.row.original.selectedColumn === NOT_PROVIDED
              ? '(Not provided)'
              : String(info.getValue() ?? '')}
          </div>
        ),
      }),
    ];
  }, [columnHeaders, csvData, setColumnMappingTableData]);

  const areAllColumnsMapped = useMemo(() => {
    const selectedRealColumns = new Set<string>();
    const requiredSet = new Set(
      REQUIRED_CLASS_HEADERS as ReadonlyArray<string>,
    );
    return columnMappingTableData.every((row) => {
      const v = row.selectedColumn;
      const hasRealColumn = v && v !== NOT_PROVIDED;
      const isRequired = requiredSet.has(row.header);

      if (isRequired && !hasRealColumn) return false;
      if (!hasRealColumn) return true;
      if (selectedRealColumns.has(v)) return false;
      selectedRealColumns.add(v);
      return true;
    });
  }, [columnMappingTableData]);

  // transform csvData to match the mapped columns
  const onNext = useCallback(() => {
    const transformedData = csvData.map((row) => {
      const transformedRow = {} as Record<
        ClassCSVHeader,
        string | number | undefined
      >;
      columnMappingTableData.forEach((column) => {
        if (column.selectedColumn && column.selectedColumn !== NOT_PROVIDED) {
          transformedRow[column.header as keyof ClassData] =
            row[column.selectedColumn as keyof ClassData];
        } else {
          // NOT_PROVIDED or empty (non-required left unmapped)
          transformedRow[column.header as keyof ClassData] = undefined;
        }
      });
      return transformedRow;
    });
    setCSVData(transformedData as Array<ClassData>);
    setStep('validate');
  }, [csvData, columnMappingTableData, setCSVData, setStep]);

  return (
    <div className='space-y-4' ref={tableContainerRef}>
      <EnrollioTable
        ariaLabel='CSV column mappings'
        totalLabel='Columns'
        filtersLayout='inline'
        filterHeader={
          <span className='text-sm text-muted-foreground'>
            Match CSV columns to class fields
          </span>
        }
        data={columnMappingTableData}
        columns={columns}
      />
      <div className='flex justify-start gap-2'>
        <Button
          variant={'outline'}
          onClick={() => {
            setStep('select');
          }}
        >
          Back
        </Button>
        <Button onClick={onNext} disabled={!areAllColumnsMapped}>
          Validate CSV Data
        </Button>
      </div>
    </div>
  );
};

function SelectColumnCell({
  value,
  onChange,
  headers,
  boundaryElement,
  isRequired,
}: {
  value: string;
  headers: Array<string>;
  onChange: (value: string) => void;
  boundaryElement?: HTMLElement | null;
  isRequired: boolean;
}) {
  const [open, setOpen] = useState(false);

  const displayValue =
    value === NOT_PROVIDED
      ? '(Not provided)'
      : value
        ? (headers.find((header) => header === value) ?? value)
        : 'Select header...';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant='outline'
          role='combobox'
          aria-expanded={open}
          className={cn(
            'min-w-[200px] justify-between text-ellipsis',
            isRequired && !value && 'border-red-500',
          )}
        >
          {displayValue}
          <ChevronsUpDown className='opacity-50' />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className='min-w-[200px] p-0'
        hideWhenDetached
        collisionBoundary={boundaryElement}
      >
        <Command>
          <CommandInput placeholder='Search header...' className='h-9' />
          <CommandList>
            <CommandEmpty>No header found.</CommandEmpty>
            <CommandGroup>
              {!isRequired && (
                <CommandItem
                  value={NOT_PROVIDED}
                  onSelect={() => {
                    onChange(value === NOT_PROVIDED ? '' : NOT_PROVIDED);
                    setOpen(false);
                  }}
                >
                  (Not provided)
                  <Check
                    className={cn(
                      'ml-auto',
                      value === NOT_PROVIDED ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                </CommandItem>
              )}
              {headers.map((header) => (
                <CommandItem
                  key={header}
                  value={header}
                  onSelect={() => {
                    onChange(value === header ? '' : header);
                    setOpen(false);
                  }}
                >
                  {header}
                  <Check
                    className={cn(
                      'ml-auto',
                      value === header ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
